import { debounce } from "lodash";
import { stringifyUrl } from "query-string";
import { PlaceResult, PlacesSearchResponse } from "../types";
import { isCoordinates } from "../useMapCenter";
import { GeoResult, GeoService } from "./geoservices";

const getNameFromResult = (result: PlaceResult): string => {
  const title = result.place_name_en || result.place_name || "";

  const [firstPart, secondPart] = title.split(", ");

  if (firstPart && secondPart) return `${firstPart}, ${secondPart}`;

  return title;
};

export const convertPlaceResultToGeoResult = (
  result: PlaceResult
): GeoResult => ({
  name: getNameFromResult(result),
  address: result.place_name_en || result.place_name,
  center: result.center,
  id: result.id,
  raw: result,
  types: result.place_type,
  components: result.context
    .filter((c) => c.id.startsWith("country"))
    .map((c) => ({
      longName: c.text_en,
      shortName: c.short_code || "",
      type: "country"
    }))
});

const search: GeoService["search"] = async (term, biasing?) => {
  const parsedTerm = isCoordinates(term) ? term.join(",") : term;
  const url = stringifyUrl({
    url: `https://api.mapbox.com/geocoding/v5/mapbox.places/${parsedTerm}.json`,
    query: {
      access_token: process.env.REACT_APP_MAPBOX_KEY,
      language: "en",
      types: "place,address,postcode",
      ...(biasing
        ? {
            proximity: biasing.center.join(",")
          }
        : {})
    }
  });
  const response = await fetch(url);

  if (!response.ok) {
    console.error(response);
    return [];
  }

  const jsonResponse: PlacesSearchResponse = await response.json();
  const results: PlaceResult[] = jsonResponse.features;

  return results.map((r) => convertPlaceResultToGeoResult(r));
};

const debouncedSearch = debounce(search, 350, {
  leading: true
});

export const mapBoxGeoService: GeoService = {
  search,
  debouncedSearch
};
